<script setup lang="ts">
import type { LearningResource } from '~/composables/useCourse';
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { LegacyEvents } from '~/enums/tracking/tracking-events.enums';
const { dispatchMessage } = useSystemMessages();

const downloading = ref(false);

const emit = defineEmits<{
  (e: 'downloaded', resource: LearningResource): void;
}>();

interface MediaDownload {
  expires: string;
  mimeType: string;
  size: number;
  url: string;
}

const props = defineProps<{
  resource: LearningResource;
}>();

const humanReadableBytes = (bytes: number) => {
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  if (bytes === 0) {
    return '0 Byte';
  }
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${parseFloat((bytes / Math.pow(1024, i)).toFixed(2))} ${sizes[i]}`;
};

const downloadResource = async (event: PointerEvent, fileName: string, id: LearningResource['uuid']) => {
  event.preventDefault();

  if (downloading.value) {
    return;
  }

  downloading.value = true;

  try {
    const resourceUrl = await getResourceUrl(id);

    if (!resourceUrl) {
      return;
    }

    const response = await $fetch<Blob>(resourceUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/octet-stream',
      },
    });

    const url = window.URL.createObjectURL(response);

    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    link.style.display = 'none';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // workaround for ios losing the reference
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
    }, 200);

    emit('downloaded', props.resource);

    // TODO: legacy event, should be removed after all the refactoring is done and the new events are in place.
    useTracking().dispatchTrackingEvent(LegacyEvents.MmGa4ResourceDownloaded, {
      resource_name: fileName,
    });

  } catch (error) {
    dispatchMessage('Failed to download resource.', 'error');
  } finally {
    downloading.value = false;
  }
};

const getResourceUrl = async (id: LearningResource['uuid']) => {
  const apiUrl = useApiUrl(`/media/${id}/download`);
  const response = await useAuthenticatedFetch<MediaDownload>(apiUrl);

  if (!response.data.value) {
    return null;
  }

  return response.data.value.url;
};
</script>

<template>
  <a
    :href="resource.type !== 'url' ? `javascript:void(0)` : resource.source"
    target="_blank"
    :title="resource.name"
    v-on="resource.type !== 'url' ? {click: (event: PointerEvent) => downloadResource(event, resource.name, resource.uuid)} : {}"
  >
    <Button
      class="w-full rounded-xl bg-white hover:bg-neutral-10 text-neutral-90 border-none pl-4"
      :disabled="downloading"
    >
      <div class="flex items-center justify-between flex-nowrap w-full gap-4">
        <div class="overflow-hidden text-ellipsis text-nowrap flex flex-col items-start gap-2">
          <div class="font-bold text-14 overflow-hidden text-ellipsis text-nowrap">
            {{resource.name}}
          </div>

          <div
            v-if="resource.metaData.fileSize"
            class="opacity-75"
          >
            Size: {{humanReadableBytes(resource.metaData.fileSize)}}
          </div>
        </div>

        <FontAwesomeIcon
          class="text-20"
          :icon="downloading ? 'fa-circle-notch' : resource.type === 'url' ? 'fa-link' : 'fa-cloud-arrow-down'"
          :class="downloading ? 'animate-spin' : ''"
        />
      </div>
    </Button>
  </a>
</template>
